import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'hooks/useCustomQuery';
import axios from 'axios';
import AGGridMain from '../../../../components/tables/AGGrid/AGGridMain';
import {
    Box,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    HStack,
} from '@chakra-ui/react';
import CountryField from './formFields/CountryField';
import NameField from './formFields/NameField';
import PrefixField from './formFields/PrefixField';
import PriorityField from './formFields/PriorityField';
import ActiveCheckbox from './formFields/ActiveCheckbox';
import MinCostField from './formFields/MinCostField';
import FlagFallInRateField from './formFields/FlagFallInRateField';
import FlagFallOutRateField from './formFields/FlagFallOutRateField';
import InboundRateField from './formFields/InboundRateField';
import OutboundRateField from './formFields/OutboundRateField';
import { Button } from 'components/v4';
import ActionMenu from 'components/presentational/ActionMenu';
import { EditIcon } from '@chakra-ui/icons';
import { TbTrash } from 'react-icons/tb';

const InternationalRate = ({ childId }) => {
    const gridRef = useRef();
    const navigate = useNavigate();
    // Use childId from props if provided, else fallback to Redux
    const child = useSelector((state) => state.table?.child);
    const effectiveChildId = childId || child?.id;
    const [isModalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [formState, setFormState] = useState({
        country: '',
        name: '',
        prefix: '',
        priority: '',
        isActive: true,
        minPrice: 0,
        flagFallInRate: 0,
        flagFallOutRate: 0,
        inboundRate: 0,
        outboundRate: 0,
        internationalRateListID: effectiveChildId || null,
    });
    const [formErrors, setFormErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Fetch rates data using TanStack Query with dynamic child id
    const {
        data: ratesData = [],
        isLoading,
        isFetching,
        error,
        refetch,
    } = useQuery({
        queryKey: ['internationalRates', effectiveChildId],
        queryFn: () => fetchRates(effectiveChildId),
        enabled: !!effectiveChildId,
    });

    const modalState = useSelector((state) => state.modal?.state || {});
    const { darkMode } = useSelector((state) => state.settings);

    // Fetch modal data using TanStack Query with dynamic modalState.id
    const { error: modalError } = useQuery({
        queryKey: ['internationalRateModal', modalState?.id],
        queryFn: () => fetchModalData(modalState?.id),
        enabled: modalMode === 'edit' && !!modalState?.id,
        onSuccess: (data) => {
            if (modalMode === 'edit') {
                setFormState({
                    country: data.country ?? '',
                    name: data.name ?? '',
                    prefix: data.prefix ?? '',
                    priority: data.priority ?? '',
                    isActive: data.isActive ?? false,
                    minPrice: data.minPrice ?? '',
                    flagFallInRate: data.flagFallInRate ?? '',
                    flagFallOutRate: data.flagFallOutRate ?? '',
                    inboundRate: data.inboundRate ?? '',
                    outboundRate: data.outboundRate ?? '',
                    internationalRateListID: effectiveChildId || null,
                });
            }
        },
    });
    const handleDelete = async (row) => {
        if (
            window.confirm(
                `Are you sure you want to delete ${row.country} ${row.name}?`,
            )
        ) {
            try {
                // Set isActive to false/0
                await axios.put('/internationalrate', {
                    ...row,
                    isActive: false,
                });
                refetch();
            } catch (err) {
                alert(
                    'Error deleting rate: ' +
                        (err?.response?.data?.message || 'Unknown error'),
                );
            }
        }
    };

    const columns = [
        {
            headerName: 'Country',
            field: 'country',
            flex: 1,
            sort: 'asc',
            sortIndex: 0,
        },
        { headerName: 'Name', field: 'name', flex: 1 },
        { headerName: 'Prefix', field: 'prefix', flex: 1 },
        {
            headerName: 'Priority',
            field: 'priority',
            flex: 1,
            sort: 'asc',
            sortIndex: 0,
        },
        {
            headerName: 'Minimum Price',
            field: 'minPrice',
            flex: 1,
            valueFormatter: (params) => {
                if (params.value == null) return '';
                return parseFloat(params.value).toFixed(4);
            },
        },
        { headerName: 'Flag Fall In Rate', field: 'flagFallInRate', flex: 1 },
        { headerName: 'Flag Fall Out Rate', field: 'flagFallOutRate', flex: 1 },
        { headerName: 'Inbound Rate', field: 'inboundRate', flex: 1 },
        { headerName: 'Outbound Rate', field: 'outboundRate', flex: 1 },
        {
            headerName: 'Action',
            lockPosition: 'right',
            width: 100,
            minWidth: 100,
            maxWidth: 100,
            sortable: false,
            filter: false,
            suppressMovable: true,
            lockVisible: true,
            suppressColumnsToolPanel: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressSizeToFit: true,
            enableRowGroup: false,
            resizable: false,
            headerClass: 'ag-action-header-center',
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }),
            cellRenderer: (params) => {
                const actionItems = [
                    {
                        icon: EditIcon,
                        label: 'Edit',
                        onClick: () => handleEdit(params.data),
                    },
                    {
                        icon: TbTrash,
                        label: 'Delete',
                        onClick: () => handleDelete(params.data),
                    },
                ];
                return <ActionMenu actionItems={actionItems} />;
            },
        },
    ];
    // Action handlers
    const handleCreate = () => {
        setModalMode('create');
        setModalOpen(true);
        setFormState({
            country: '',
            name: '',
            prefix: '',
            priority: '',
            isActive: true,
            minPrice: 0,
            flagFallInRate: 0,
            flagFallOutRate: 0,
            inboundRate: 0,
            outboundRate: 0,
            internationalRateListID: effectiveChildId || null,
        });
    };
    const handleEdit = (row) => {
        setModalMode('edit');
        setFormState(row);
        setModalOpen(true);
    };
    const validateForm = () => {
        const errors = {};
        if (!formState.country) errors.country = 'Country is required';
        if (!formState.name) errors.name = 'Name is required';
        if (!formState.prefix) errors.prefix = 'Prefix is required';
        if (!formState.priority) errors.priority = 'Priority is required';
        if (
            formState.minPrice === '' ||
            formState.minPrice === null ||
            formState.minPrice === undefined
        )
            errors.minPrice = 'Minimum Cost is required';
        if (
            formState.flagFallInRate === '' ||
            formState.flagFallInRate === null ||
            formState.flagFallInRate === undefined
        )
            errors.flagFallInRate = 'Flag Fall In Rate is required';
        if (
            formState.flagFallOutRate === '' ||
            formState.flagFallOutRate === null ||
            formState.flagFallOutRate === undefined
        )
            errors.flagFallOutRate = 'Flag Fall Out Rate is required';
        if (
            formState.inboundRate === '' ||
            formState.inboundRate === null ||
            formState.inboundRate === undefined
        )
            errors.inboundRate = 'Inbound Rate is required';
        if (
            formState.outboundRate === '' ||
            formState.outboundRate === null ||
            formState.outboundRate === undefined
        )
            errors.outboundRate = 'Outbound Rate is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        setApiError('');
        if (!validateForm()) return;
        setIsSaving(true);
        try {
            if (modalMode === 'create') {
                // Create API call
                // Remove fields that are empty before sending
                const payload = Object.fromEntries(
                    Object.entries(formState).filter(
                        // eslint-disable-next-line no-unused-vars
                        ([_, v]) => v !== '' && v !== null && v !== undefined,
                    ),
                );
                await axios.post('/internationalrate', payload); // Update endpoint as needed
            } else {
                // Edit API call
                await axios.put('/internationalrate', formState); // Use local editId if available
            }
            setModalOpen(false);
            refetch();
        } catch (err) {
            setApiError(err?.response?.data?.message || 'Error saving data');
        } finally {
            setIsSaving(false);
        }
    };
    const handleCancel = () => {
        setModalOpen(false);
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormState({ ...formState, [name]: value });
    };
    const handleCheckboxChange = (e) => {
        setFormState({ ...formState, isActive: e.target.checked });
    };

    // Define columns for AGGridMain

    return (
        <>
            <AGGridMain
                gridRef={gridRef}
                tableData={{ columns, data: ratesData }}
                refetch={refetch}
                isLoading={isLoading}
                isFetching={isFetching}
                stateIdentifier="internationalRates"
                extraComponents={
                    <div style={{ paddingLeft: '20px' }}>
                        <HStack spacing={2}>
                            <Button size="sm" onClick={handleCreate}>
                                + Add Rate
                            </Button>
                            <Button
                                size="sm"
                                onClick={() =>
                                    navigate(
                                        `/international-rates/csv?listId=${effectiveChildId}`,
                                    )
                                }>
                                CSV Upload
                            </Button>
                        </HStack>
                    </div>
                }
            />
            {error && <Box color="red.500">Error loading rates data.</Box>}
            {/* Modal for create/edit */}
            <Modal
                bg={darkMode ? '#2e2e30' : 'white'}
                color={darkMode ? 'white' : 'black'}
                isOpen={isModalOpen}
                onClose={handleCancel}
                isCentered
                size="xl">
                <ModalOverlay />
                <ModalContent
                    bg={darkMode ? '#2e2e30' : 'white'}
                    color={darkMode ? 'white' : 'black'}>
                    <ModalHeader
                        color={darkMode ? 'white' : 'black'}
                        bg={darkMode ? '#2e2e30' : 'white'}>
                        {modalMode === 'edit'
                            ? `Edit ${formState.country} ${formState.name} Int'l Rate`
                            : "Add Int'l Rate"}
                    </ModalHeader>
                    <ModalBody
                        bg={darkMode ? '#2e2e30' : 'white'}
                        color={darkMode ? 'white' : 'black'}>
                        {/* Spinner only for edit mode and loading, form always visible for create */}
                        {/* {modalMode === 'edit' && isModalLoading ? (
              <Box display="flex" alignItems="center" justifyContent="center" minHeight="100px">
                <Spinner size="lg" />
              </Box>
            ) : ( */}
                        <Box>
                            {/* Validation and API error messages */}
                            {apiError && (
                                <Box color="red.500" mb={2}>
                                    {apiError}
                                </Box>
                            )}
                            {/* Row 1: Country, Name, Prefix */}
                            <Box display="flex" gap={4} mb={4}>
                                <CountryField
                                    value={formState.country}
                                    onChange={handleInputChange}
                                    error={formErrors.country}
                                    style={{ flex: 1 }}
                                />
                                <NameField
                                    value={formState.name}
                                    onChange={handleInputChange}
                                    error={formErrors.name}
                                    style={{ flex: 1 }}
                                />
                                <PrefixField
                                    value={formState.prefix}
                                    onChange={handleInputChange}
                                    error={formErrors.prefix}
                                    style={{ flex: 1 }}
                                />
                            </Box>
                            {/* Row 2: Active, Priority */}
                            <Box
                                display="flex"
                                gap={4}
                                mb={4}
                                alignItems="center">
                                {modalMode === 'edit' && (
                                    <Box
                                        flex={1}
                                        display="flex"
                                        alignItems="center">
                                        <ActiveCheckbox
                                            value={formState.isActive}
                                            onChange={handleCheckboxChange}
                                        />
                                    </Box>
                                )}
                                <Box flex={1}>
                                    <PriorityField
                                        value={formState.priority}
                                        onChange={handleInputChange}
                                        error={formErrors.priority}
                                    />
                                </Box>
                                <Box flex={1}></Box>
                            </Box>
                            {/* Row 3: Minimum Cost, Flag Fall In Rate, Flag Fall Out Rate */}
                            <Box display="flex" gap={4} mb={4}>
                                <Box flex={1}>
                                    <MinCostField
                                        value={formState.minPrice}
                                        onChange={handleInputChange}
                                        error={formErrors.minPrice}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <FlagFallInRateField
                                        value={formState.flagFallInRate}
                                        onChange={handleInputChange}
                                        error={formErrors.flagFallInRate}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <FlagFallOutRateField
                                        value={formState.flagFallOutRate}
                                        onChange={handleInputChange}
                                        error={formErrors.flagFallOutRate}
                                    />
                                </Box>
                            </Box>
                            {/* Row 4: Inbound Rate, Outbound Rate */}
                            <Box display="flex" gap={4} mb={2}>
                                <Box flex={1}>
                                    <InboundRateField
                                        value={formState.inboundRate}
                                        onChange={handleInputChange}
                                        error={formErrors.inboundRate}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <OutboundRateField
                                        value={formState.outboundRate}
                                        onChange={handleInputChange}
                                        error={formErrors.outboundRate}
                                    />
                                </Box>
                                <Box flex={1}></Box>
                            </Box>
                        </Box>
                        {/* )} */}
                        {modalError && (
                            <Box color="red.500" mb={2}>
                                Error loading modal data.
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="outline"
                            mr={3}
                            onClick={handleCancel}
                            isDisabled={isSaving}>
                            Close
                        </Button>
                        <Button colorScheme="blue" onClick={handleSave}>
                            {isSaving
                                ? 'Saving...'
                                : modalMode === 'edit'
                                  ? 'Update'
                                  : 'Create'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

const fetchRates = async (id) => {
    if (!id) return [];
    const response = await axios.get(`/internationalrates/${id}`); // Update with your actual API endpoint
    return response.data;
};

const fetchModalData = async (id) => {
    if (!id) return {};
    const response = await axios.get(`/internationalrate/${id}`); // Update with your actual API endpoint
    return response.data;
};

export default InternationalRate;
